import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

import Cookies from "js-cookie";
import TriggerHelp from "../components/TriggerHelp";

import {
  keccak256_hash_string,
  sha3_256_hash_string,
  HASH_ALGORITHM_SHA3_256,
  HASH_ALGORITHM_KECCAK_256,
} from "../utils/cryptoUtils";
import DownloadStampCertificate from "./DownloadStampCertificate";
import { formatHexStr } from "../utils/formatHexStr";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import {
  Image,
  Text,
  useMantineTheme,
  CopyButton,
  Tooltip,
  ActionIcon,
  Accordion,
} from "@mantine/core";
import { useStylesStampResult } from "../utils/mantine/useStylesStampResult";
import { Box, Flex, Title } from "@mantine/core";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import { IconCheck, IconCopy, IconStack2 } from "@tabler/icons-react";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import StampDetail from "./StampDetail";
import { useStylesAccordion } from "../utils/mantine/useStylesAccordion";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";
import { StampTimeDetailsRows } from "./StampIdentityFields";

const StampFound = ({
  data,
  staticBasePath,
  handleDefaultScreen,
  showVerifyStamp = true,
  allHashes,
}) => {
  //css classes
  const { classes: classesStampResult } = useStylesStampResult();
  const { classes: classesPage } = useStylesPage();
  const { classes: tooltipClasses } = useStylesTooltip();
  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();
  const { classes: classesAccordion } = useStylesAccordion();
  const { classes: classesIcons } = useStylesIcons();

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // in development
  const [pdfShow] = useState(true);

  // current hash
  const [currentHashVersion, setCurrentHashVersion] = useState("");

  // current hash Collection
  const [currentHashCollectionVersion, setCurrentHashCollectionVersion] =
    useState("");

  useEffect(() => {
    setCurrentHashVersion("");
    if (allHashes[data.objectCid]) {
      setCurrentHashVersion(allHashes[data.objectCid]);
    }

    // check collection hash type
    if (data.collection_name) {
      const hashCollectionSha3 = sha3_256_hash_string(data.collection_name);
      const hashCollectionKeccak256 = keccak256_hash_string(
        data.collection_name,
      );

      const supportedHashTypesCollection = {
        [hashCollectionSha3]: HASH_ALGORITHM_SHA3_256,
        [hashCollectionKeccak256]: HASH_ALGORITHM_KECCAK_256,
      };

      if (supportedHashTypesCollection[data.collection_hash]) {
        //check exists current hash Collection in all hashes
        setCurrentHashCollectionVersion(
          supportedHashTypesCollection[data.collection_hash],
        );
      }
    }
  }, [allHashes]);

  // Logged-out users have no display preference, so format and label times in UTC.
  const timeZone = data.display_timezone ? data.display_timezone : "UTC";

  const [helpIsActive, setHelpIsActive] = useState(() => {
    const cookieValue = Cookies.get("helpIsActive");
    return cookieValue !== undefined ? JSON.parse(cookieValue) : true;
  });

  const handleHelpTriggerClick = () => {
    const newHelpIsActive = !helpIsActive;
    setHelpIsActive(newHelpIsActive);
    Cookies.set("helpIsActive", JSON.stringify(newHelpIsActive));
  };

  const [isNearBottom, setIsNearBottom] = useState(false);
  const showDuplicateStampWarning = data.showDuplicateStampWarning;

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const remainingScroll = documentHeight - (scrollTop + windowHeight);

    if (
      (remainingScroll <= 80 && window.innerWidth <= 639) ||
      (remainingScroll <= 48 && window.innerWidth >= 640)
    ) {
      setIsNearBottom(true);
    } else {
      setIsNearBottom(false);
    }
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return (
    <Box
      className={`${classesStampResult.stamp} ${
        classesPage.page
      } stamp-result ${
        showDuplicateStampWarning ? "stamp-result--duplicate-warning" : ""
      }`}
    >
      <Image
        src={`${staticBasePath}public/images/svg/vb-icon-verified.svg`}
        alt="Stamp Found!"
        className={classesPage.pageHeaderIcon}
      />

      <Title order={1}>Stamp Found!</Title>
      {showDuplicateStampWarning && (
        <Flex className="duplicate-stamp-warning">
          <IconStack2 className="duplicate-stamp-warning__icon" />
          <Text className="duplicate-stamp-warning__text">
            <Text component="span" className="duplicate-stamp-warning__lead">
              This file has been stamped multiple times.
            </Text>{" "}
            You&apos;re viewing the earliest stamp. Other stamps may be by
            different users or in different collections.
          </Text>
        </Flex>
      )}
      <StampDetail
        data={data}
        staticBasePath={staticBasePath}
        helpIsActive={helpIsActive}
        timeZone={timeZone}
      ></StampDetail>
      <Box
        className={`${classesTechnicalDetail.technicalBlock} ${classesTechnicalDetail.technical}`}
      >
        <Accordion
          className={classesAccordion.accordion}
          value="tech"
          variant="contained"
          chevron={null}
        >
          <Accordion.Item value="tech">
            <Accordion.Control component="div">
              <Flex gap={theme.flex.gap.xs}>
                <Text className="title">Stamp Details</Text>

                {helpIsActive && (
                  <CustomTooltip
                    content={
                      <>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Blockchain Address
                          </Text>{" "}
                          is the blockchain address of the user who made the
                          stamp
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Content ID
                          </Text>{" "}
                          is the fingerprint, or hash, of the stamped data that
                          has been published to a blockchain
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Collection ID
                          </Text>{" "}
                          is the fingerprint, or hash, of the Collection Name to
                          which this stamp belongs
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Public Blockchain Record
                          </Text>{" "}
                          is a link to a third-party tool showing the public
                          record of this stamp’s location
                        </Text>
                      </>
                    }
                  ></CustomTooltip>
                )}
              </Flex>
            </Accordion.Control>
            <Accordion.Panel>
              <Box>
                <StampTimeDetailsRows data={data} />

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">Blockchain Address</Text>
                  <Box className="dots"></Box>
                  <Text className="value" id="stamp-found__account_id">
                    <CustomAnchor
                      text={formatHexStr(data.user_id)}
                      componentClasses="link"
                      color={theme.customColors.black}
                      href={`/verify/user-data/?user=${data.user_id}`}
                    />
                    <CopyButton value={data.user_id} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">
                    Content ID{" "}
                    {currentHashVersion && <>({currentHashVersion})</>}
                  </Text>
                  <Box className="dots"></Box>
                  <Text className="value" id="stamp-found__content-id">
                    {formatHexStr(data.objectCid)}

                    <CopyButton value={data.objectCid} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">
                    {" "}
                    Collection ID{" "}
                    {currentHashCollectionVersion && (
                      <>({currentHashCollectionVersion})</>
                    )}
                  </Text>
                  <Box className="dots"></Box>
                  <Text className="value">
                    {data.collection_hash ? (
                      <CustomAnchor
                        text={
                          data.collection_hash
                            ? formatHexStr(data.collection_hash)
                            : "<<None>>"
                        }
                        componentClasses="link collection-hash"
                        color={theme.customColors.black}
                        href={`/verify/user-data/?user=${
                          data.user || data.user_id
                        }&collection_cid=${data.collection_hash}`}
                      />
                    ) : (
                      "<<None>>"
                    )}
                    <CopyButton
                      value={data.collection_hash ?? ""}
                      timeout={2000}
                    >
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            style={{
                              opacity: data.collection_hash ? 1 : 0,
                              pointerEvents: data.collection_hash
                                ? "auto"
                                : "none",
                            }}
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">Public Blockchain Record</Text>
                  <Box className="dots"></Box>
                  <Text className="value">
                    <CustomAnchor
                      target="_blank"
                      text={`${data.blockExplorerUrl}${formatHexStr(
                        data.transactionHash,
                        10,
                        0,
                        20,
                        true,
                      )}...`}
                      componentClasses="link"
                      color={theme.customColors.black}
                      href={`${data.blockExplorerUrl}${data.transactionHash}`}
                    />

                    <CopyButton
                      value={`${data.blockExplorerUrl}${data.transactionHash}`}
                      timeout={2000}
                    >
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                {pdfShow && (
                  <Flex
                    gap={theme.flex.gap.xxs}
                    justify={theme.flex.justify.between}
                    className="item download-pdf-item"
                    align={theme.flex.align.end}
                  >
                    {/* PDF display_timezone is now always UTC (formatted server-side). */}
                    <DownloadStampCertificate data={data} />
                  </Flex>
                )}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>

      {showVerifyStamp === true && (
        <Flex
          align={theme.flex.align.center}
          justify={theme.flex.justify.center}
          mt={theme.margin.xxlEm}
          gap={theme.flex.gap.xs}
        >
          <CustomButton
            text="Verify Another Stamp"
            onClick={handleDefaultScreen}
            componentClasses="buttonBlack buttonBlack-verify anchor"
          ></CustomButton>
        </Flex>
      )}

      <TriggerHelp
        helpIsActive={helpIsActive}
        isNearBottom={isNearBottom}
        isAuthenticated="true"
        isOpenNotification={false}
        handleHelpTriggerClick={handleHelpTriggerClick}
      />
    </Box>
  );
};

StampFound.propTypes = {
  data: PropTypes.shape({
    objectCid: PropTypes.string,
    time: PropTypes.string,
    user: PropTypes.string,
    user_id: PropTypes.string,
    authenticated: PropTypes.bool,
    collection_name: PropTypes.string,
    timedelta: PropTypes.string,
    collection_hash: PropTypes.string,
    blockExplorerUrl: PropTypes.string,
    transactionHash: PropTypes.string,
    timestamp: PropTypes.string,
    display_timezone: PropTypes.string,
    user_at_stamp_time: PropTypes.string,
    verified_at_stamp_time: PropTypes.bool,
    verified_since_at_stamp_time: PropTypes.string,
    user_changed_since_stamp: PropTypes.bool,
    identity_changed_since_stamp: PropTypes.bool,
    showDuplicateStampWarning: PropTypes.bool,
  }).isRequired,
  staticBasePath: PropTypes.string.isRequired,
  handleDefaultScreen: PropTypes.func.isRequired,
  showVerifyStamp: PropTypes.bool,
  allHashes: PropTypes.object.isRequired,
};
export default StampFound;
